import { useEffect, useMemo, useState } from 'react';
import { useSelfUserProfile } from 'apis/CompanyAPI/users/useSelfUserProfile';
import useCommunityRole from 'hooks/useCommunityRole';
import { getOrUndefined } from 'util/resource';
import type { PostAs } from 'domain/Updates/shared/types';
import type { TinyCommunity } from 'types/company/community';
import { asCompanyAuthor, asUserAuthor, Author } from 'domain/shared/author';
import { SelfUserProfile } from 'types/user';

export function usePostAs(
  community: TinyCommunity,
  postTo?: { visibility?: string },
  initialPostingAs?: PostAs,
): {
  postingAs: PostAs | undefined;
  setPostingAs: (as: PostAs | undefined) => void;
  author: Author;
} {
  const { resource: userResource } = useSelfUserProfile();
  const user = getOrUndefined(userResource);

  const { myCompanyMemberships: memberships, hasScope } = useCommunityRole(community.slug);

  const myCompanyMemberships = useMemo(
    () => memberships.filter(m => m.visibility === 'Visible').map(m => m.company),
    [memberships],
  );

  const canPostAsSeveral = useMemo(() => {
    if (postTo?.visibility === 'Founders' || postTo?.visibility === 'CommunityMembersExceptFounders') {
      return false;
    }

    const admin = hasScope('Write', 'Update', 'Admin') ? 1 : 0;
    const self = hasScope('Write', 'Update', 'Self') ? 1 : 0;
    const company = myCompanyMemberships.length > 0 ? 1 : 0;

    return admin + self + company > 1;
  }, [postTo?.visibility, hasScope, myCompanyMemberships]);

  const [postingAs, setPostingAs] = useState<PostAs | undefined>(initialPostingAs);

  // Sync initialPostingAs to state when it's provided
  useEffect(() => {
    if (initialPostingAs) {
      setPostingAs(initialPostingAs);
    }
  }, [initialPostingAs]);

  // Auto-calculate postingAs only when not provided and not yet set
  useEffect(() => {
    // Don't auto-calculate if initialPostingAs was provided
    if (initialPostingAs) {
      return;
    }

    // If already set (either manually or by previous auto-calc), don't override
    if (postingAs) {
      return;
    }

    // Auto-calculate when only one option available
    const autoCalculated = !canPostAsSeveral
      ? hasScope('Write', 'Update', 'Admin')
        ? { type: 'community' as const, community }
        : myCompanyMemberships.length === 1
          ? { type: 'company' as const, company: myCompanyMemberships[0] }
          : hasScope('Write', 'Update', 'Self') && user
            ? { type: 'user' as const, user }
            : undefined
      : undefined;

    if (autoCalculated) {
      setPostingAs(autoCalculated);
    }
  }, [initialPostingAs, canPostAsSeveral, hasScope, community, myCompanyMemberships, user, postingAs]);

  const author = useMemo(() => {
    return getAuthorFromPostAs(postingAs, user, community);
  }, [postingAs, user, community]);

  return { postingAs, setPostingAs, author };
}

function getAuthorFromPostAs(
  postingAs: PostAs | undefined,
  user: SelfUserProfile | undefined,
  community: TinyCommunity,
): Author {
  if (postingAs?.type === 'company') {
    return asCompanyAuthor(user, postingAs.company);
  }

  if (postingAs?.type === 'community') {
    return asCompanyAuthor(user, community);
  }

  return asUserAuthor(user);
}
